import { useState } from 'react';
import { DropdownField, InlineNotification, Modal, TextField, useFormField } from '@frontend/design-system';
import { useUpdateSubscriptionStatus, SUBSCRIPTION_STATUSES, type SubscriptionStatus } from '../index';
import { truelarkStyles as styles } from '../styles';

type Props = {
  locationId: string;
  currentStatus: SubscriptionStatus | '';
  updatedBy: string;
  onClose: () => void;
  onSuccess: () => void;
};

export function UpdateSubscriptionModal({ locationId, currentStatus, updatedBy, onClose, onSuccess }: Props) {
  const statusFieldProps = useFormField({ type: 'dropdown', value: currentStatus }, [currentStatus]);
  const reasonFieldProps = useFormField({ type: 'text' });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const mutation = useUpdateSubscriptionStatus(locationId, {
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: () => setErrorMessage('Failed to update subscription status. Please try again.'),
  });

  const handleUpdate = () => {
    if (!statusFieldProps.value) return;
    mutation.mutate({
      tl_business_id: locationId,
      subscription_status: statusFieldProps.value as SubscriptionStatus,
      updated_by: updatedBy,
      ...(reasonFieldProps.value ? { update_reason: reasonFieldProps.value } : {}),
      send_email_notification: false,
    });
  };

  return (
    <>
      <Modal.Header onClose={onClose}>Update Subscription Status</Modal.Header>
      <Modal.Body>
        {errorMessage && <InlineNotification status='error' message={errorMessage} />}
        <div css={styles.modalBody}>
          <DropdownField name='subscription_status' label='New Status' {...statusFieldProps}>
            {SUBSCRIPTION_STATUSES.map(({ value, label }) => (
              <DropdownField.Option key={value} value={value}>
                {label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          <TextField name='update_reason' label='Reason' {...reasonFieldProps} />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: 'Update',
          onClick: handleUpdate,
          disabled: !statusFieldProps.value,
          loading: mutation.isLoading,
        }}
        secondary={{ label: 'Cancel', onClick: onClose }}
      />
    </>
  );
}
